import CloseIcon from '@assets/images/icons/modal/close.svg?react'
import { SyncStatusIndicator } from '@components/ArtBoard/Header/components'
import { DropdownContainer, NewTooltip } from '@lib/components'
import { Link as LinkIcon } from '@octopus-do/octopus-storybook/icons'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import cx from 'classnames'
import type React from 'react'
import type { ReactNode } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { DEFAULT_PAGE_NAME } from '@/constants/defaultNames'
import { GlobalActions } from '@/store'
import css from './ModalHeader.module.scss'

type Props = {
  icon?: React.ReactNode
  title?: ReactNode | string
  additionalHeaderActions?: ReactNode
  copyUrl?: string
  onlyClose?: boolean
  showBorder?: boolean
  showShadow?: boolean
  color?: string
  withSyncStatus?: boolean
  renderMenu?: (closeDropdown: () => void) => ReactNode
  onBackButtonClick?: () => void
  onClose: () => void
  onTitleClick?: () => void
  onClickOptions?: () => void
}

interface TittleProps {
  onTitleClick?: () => void
  icon?: React.ReactNode
  title?: ReactNode | string
  color?: string
}
const Tittle: React.FC<TittleProps> = ({
  onTitleClick,
  title,
  icon,
  color,
}) => {
  if (!onTitleClick) {
    return (
      <div
        className={cx(css.titleButton, {
          [css.isClickable]: !!onTitleClick,
        })}
        style={{ color }}
      >
        {icon && <div className={css.icon}>{icon}</div>}
        {title && <div className={cx(css.title)}>{title}</div>}
      </div>
    )
  }

  return (
    <button
      className={cx(css.titleButton, {
        [css.isClickable]: !!onTitleClick,
      })}
      onClick={onTitleClick}
      style={{ color }}
    >
      {icon && <div className={css.icon}>{icon}</div>}
      {title && <div className={cx(css.title)}>{title}</div>}
    </button>
  )
}

export const ModalHeader: React.FC<Props> = ({
  icon,
  title = DEFAULT_PAGE_NAME,
  additionalHeaderActions,
  copyUrl,
  onlyClose,
  showBorder,
  showShadow,
  color,
  withSyncStatus,
  renderMenu,
  onBackButtonClick,
  onClose,
  onTitleClick,
  onClickOptions,
}) => {
  const dispatch = useDispatch()
  const projectId = useSelector(migrationProjectSelector.getProjectId)

  const handleCopy = () => {
    if (!projectId || !copyUrl) {
      return
    }
    dispatch(GlobalActions.copy({ value: copyUrl }))
  }

  return (
    <div
      className={cx(css.wrap, {
        [css.borderBottom]: showBorder,
        [css.boxShadow]: showShadow,
      })}
    >
      {!onlyClose && (
        <>
          {onBackButtonClick ? (
            <NewTooltip
              content="Go back"
              offset={{ y: 4 }}
              renderEl={(onSetRef, onMouseEnter, onMouseLeave) => (
                <button
                  ref={onSetRef}
                  className={cx(css.button, css.backButton)}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                  onClick={onBackButtonClick}
                >
                  <i className="fa fa-arrow-left" /> Back
                </button>
              )}
            />
          ) : (
            <Tittle
              color={color}
              icon={icon}
              title={title}
              onTitleClick={onTitleClick}
            />
          )}
        </>
      )}

      <div className={css.rightColumn}>
        {!onlyClose && (
          <>
            {withSyncStatus && (
              <div className={css.indicator_wrap}>
                <SyncStatusIndicator />
              </div>
            )}
            {additionalHeaderActions}

            {copyUrl && (
              <NewTooltip
                content={projectId ? 'Copy link' : 'Save project to copy'}
                offset={{ y: 4 }}
                renderEl={(onSetRef, onMouseEnter, onMouseLeave) => (
                  <button
                    ref={onSetRef}
                    className={cx(css.button, css.link, {
                      [css.disabled]: !projectId,
                    })}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onClick={handleCopy}
                  >
                    <LinkIcon />
                  </button>
                )}
              />
            )}

            {renderMenu && (
              <DropdownContainer
                position="bottom-end"
                customClassName={css.dropdownContainer}
                renderButton={(onSetDropdownRef, onClick, isOpen) => (
                  <NewTooltip
                    content="Options"
                    offset={{ y: 4 }}
                    renderEl={(onSetRef, onMouseEnter, onMouseLeave) => (
                      <button
                        ref={(element) => {
                          onSetDropdownRef(element)
                          onSetRef(element)
                        }}
                        className={cx(css.button, css.dropdownButton, {
                          [css.active]: isOpen,
                        })}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        onClick={() => {
                          onClick()
                          onClickOptions?.()
                        }}
                      >
                        <i className="fa fa-ellipsis-v" />
                      </button>
                    )}
                  />
                )}
              >
                {(closeDropdown) => (
                  <div className={css.dropdownWrapper}>
                    {renderMenu?.(closeDropdown)}
                  </div>
                )}
              </DropdownContainer>
            )}
          </>
        )}

        <button className={cx(css.button, css.closeButton)} onClick={onClose}>
          <CloseIcon />
        </button>
      </div>
    </div>
  )
}
